import React, {
  ChangeEventHandler,
  Dispatch,
  FC,
  SetStateAction,
  useCallback,
  useEffect,
  useState,
} from 'react';
import { CardText, Col, FormGroup, FormText, Input, Label } from 'reactstrap';
import { VolunteerUpsertModel } from '../../../../shared/volunteer';
import {
  FormNodeVolunteerContactModel,
  VolunteerExternalContactMethod,
} from '../../../../shared/volunteer/form';
import { VolunteerFormUser } from '../../../containers/apply/volunteer/VolunteerForm';
import { omit } from '../../../utils';
import { FormActionType } from '../../form/FormReducer';

interface VolunteerContactProps {
  readonly node: FormNodeVolunteerContactModel;
  readonly state: VolunteerUpsertModel;
  readonly user: VolunteerFormUser;
  readonly dispatch: Dispatch<FormActionType>;
}

export const VolunteerContact: FC<VolunteerContactProps> = ({ node, state, user, dispatch }) => {
  const [contactMethods, setContactMethods] = useState(state.contactMethods ?? {});
  const [contactMethod, setContactMethod] = useState(state.contactMethod);

  useEffect(() => {
    dispatch({
      type: 'updateVolunteerContact',
      methods: contactMethods,
      method: contactMethod,
    });
  }, [contactMethods, contactMethod, dispatch]);

  return (
    <VolunteerContactInfo
      contactMethod={state.contactMethod}
      contactMethods={contactMethods}
      methodList={node.externalMethods ?? []}
      setContactMethod={setContactMethod}
      setContactMethods={setContactMethods}
      user={user}
    />
  );
};

interface ContactMethodInput {
  name: string;
  input?: {
    value: string;
    update: ChangeEventHandler<HTMLInputElement>;
  };
  // Displays assumed value i.e. for phone
  displayText?: string;
  // Actual name displayed as label
  displayName?: string;
}

interface VolunteerContactInfoProps {
  readonly methodList: VolunteerExternalContactMethod[];
  readonly contactMethod?: string;
  readonly contactMethods: Record<string, string>;
  readonly user: VolunteerFormUser;
  readonly setContactMethods: Dispatch<SetStateAction<Record<string, string>>>;
  readonly setContactMethod: Dispatch<SetStateAction<string | undefined>>;
}

const VolunteerContactInfo: FC<VolunteerContactInfoProps> = ({
  methodList,
  contactMethod,
  user,
  contactMethods,
  setContactMethods,
  setContactMethod,
}) => {
  const methods: ContactMethodInput[] = [
    { name: 'call', displayName: 'Call', displayText: user.phone ?? '' },
    { name: 'text', displayName: 'Text', displayText: user.phone ?? '' },
    { name: 'email', displayName: 'Email', displayText: user.email ?? '' },
  ];

  const update = useCallback(
    (name: string, value: string) => {
      setContactMethods((old) => {
        if (!value) {
          return omit(old, name);
        }

        return {
          ...old,
          [name]: value || undefined,
        };
      });
    },
    [setContactMethods],
  );

  for (const method of methodList) {
    methods.push({
      input: {
        value: contactMethods[method.name] ?? '',
        update: (e) => {
          update(method.name, e.target.value);
        },
      },
      ...method,
    });
  }

  return (
    <Col xs={12}>
      <CardText tag="div">
        <div className="mx-4">
          {methods.map(({ name, displayText, input, displayName }) => {
            const key = `contactMethod${name}`;
            return (
              <FormGroup check className="mt-10" key={name}>
                <Input
                  defaultChecked={name === contactMethod}
                  id={key}
                  name="contactMethod"
                  onChange={(e) => {
                    if (e.target.checked) {
                      setContactMethod(name);
                    }
                  }}
                  type="radio"
                  value={name}
                />
                <Label check for={key}>
                  {displayName ?? name}
                </Label>
                {displayText && <FormText color="muted">{displayText}</FormText>}
                {input && (
                  <FormText color="muted">
                    <Input id={`${key}Input`} onChange={input.update} value={input.value} />
                  </FormText>
                )}
              </FormGroup>
            );
          })}
        </div>
      </CardText>
    </Col>
  );
};
